/**
 * VectorLayoutTicket — Vector Layout ticket content for EditorPanel.
 *
 * 2026-09-02 reorganization (owner request — the previous shape stacked
 * seven collapsibles inside each layout row and scattered the label
 * workflow across three boxes):
 *
 *   • Compact LAYOUT CARDS on top: name (ellipsized), entity count, a
 *     color-coded classified-% badge, click-to-activate, × to remove.
 *   • Below them, ONE tool accordion for the ACTIVE layout — a single
 *     section open at a time: Setup (anchors) / Labels (Families → Schema
 *     → Patterns unified) / Module Grid / Display.
 *
 * The editor panels all read `data.siteLayout` (the alias), which
 * `setActiveLayoutId` keeps in sync with the active card — no per-layout
 * prop drilling (F-01B contract unchanged).
 *
 * Empty-state UI (no layouts) is preserved from F-01A. Zero layouts is a
 * valid project shape per the F-01B product decision.
 */

import { useMemo, useState } from 'react';
import { useProjectStore, useUIStore } from '../../../stores';
import { useIsReadOnlyViewer } from '../../../hooks/useShareViewer';
import { Button } from '../../common';
import { adoptCompanionOverlay } from '../../../utils/planBakeDispatch';
import ModuleGridPanel from '../../layout/ModuleGridPanel';
import LabelSchemaPanel from '../../layout/LabelSchemaPanel';
import LabelFamiliesPanel from '../../layout/LabelFamiliesPanel';
import type { SiteLayout } from '../../../domain/layout/types';
import './VectorLayoutTicket.css';

type SectionId = 'setup' | 'labels' | 'grid' | 'display';

/** Share of topology-classified labels (infrastructure excluded from both
 *  sides) — the "does this sheet still need the families pass?" number. */
function classifiedPct(layout: SiteLayout): number | null {
  const ents = layout.textEntities;
  if (ents.length === 0) return null;
  let infra = 0;
  let classified = 0;
  for (const e of ents) {
    const level = (e.classificationOverride ?? e.classification)?.level;
    if (!level) continue;
    if (level === 'infrastructure') {
      infra++;
    } else if (level !== 'unknown') {
      classified++;
    }
  }
  const denom = ents.length - infra;
  return denom > 0 ? Math.round((100 * classified) / denom) : null;
}

function pctColor(pct: number): string {
  if (pct >= 80) return '#22c55e';
  if (pct >= 40) return '#f59e0b';
  return '#ef4444';
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`editor-section__icon${open ? '' : ' editor-section__icon--collapsed'}`}
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 2l4 4-4 4" />
    </svg>
  );
}

export default function VectorLayoutTicket() {
  const isReadOnly = useIsReadOnlyViewer();
  const siteLayouts = useProjectStore((s) => s.data.siteLayouts);
  const activeLayoutId = useProjectStore((s) => s.activeLayoutId);
  const aliasLayout = useProjectStore((s) => s.data.siteLayout);
  const setActiveLayoutId = useProjectStore((s) => s.setActiveLayoutId);
  const removeSiteLayoutById = useProjectStore((s) => s.removeSiteLayoutById);
  const openModal = useUIStore((s) => s.openModal);
  const showConfirm = useUIStore((s) => s.showConfirm);
  const addNotification = useUIStore((s) => s.addNotification);
  const overlays = useProjectStore((s) => s.data.overlays);
  const [repairing, setRepairing] = useState(false);

  // One tool section open at a time; Labels is the primary workflow.
  const [openSection, setOpenSection] = useState<SectionId>('labels');
  const toggleSection = (id: SectionId) =>
    setOpenSection((prev) => (prev === id ? ('' as SectionId) : id));

  // Effective list: prefer the canonical array, fall back to the legacy
  // singleton if a project predates F-01A load-time normalization.
  const layouts = siteLayouts && siteLayouts.length > 0
    ? siteLayouts
    : (aliasLayout ? [aliasLayout] : []);

  const pctById = useMemo(
    () => new Map(layouts.map((l) => [l.id, classifiedPct(l)])),
    [layouts],
  );

  // ── Broken companion link ───────────────────────────────────────────────
  // A layout imported through the one-upload path remembers the plan sheet it
  // registers. If that sheet's creation failed, the id survives while the
  // overlay does not, and the anchor session's Done then places and bakes
  // nothing — silently, forever (Tel Yitzhak and Hoshen, 2026-08-30). Only
  // offered when the site has exactly ONE plan sheet, because with several
  // there is no way to know which one this layout belongs to.
  const activeLayout = layouts.find((l) => l.id === activeLayoutId) ?? layouts[0];
  const ghostPlanId = activeLayout?.planOverlayId
    && !overlays.some((o) => o.id === activeLayout.planOverlayId)
    ? activeLayout.planOverlayId
    : null;
  const planSheets = overlays.filter((o) => o.bake);
  const repairTarget = ghostPlanId && planSheets.length === 1 ? planSheets[0] : null;

  const handleRepairLink = async () => {
    if (!repairTarget || repairing) return;
    setRepairing(true);
    try {
      const outcome = await adoptCompanionOverlay(repairTarget.id);
      addNotification({
        type: outcome === 'linked' ? 'info' : 'success',
        title: 'Plan re-linked',
        message: outcome === 'placed-and-baking'
          ? `"${repairTarget.name}" placed from this layout's anchors and baking at full resolution.`
          : outcome === 'placed'
            ? `"${repairTarget.name}" placed from this layout's anchors.`
            : `"${repairTarget.name}" is linked; place the anchors and it will position itself.`,
      });
    } catch (err) {
      addNotification({
        type: 'error',
        title: 'Could not re-link the plan',
        message: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setRepairing(false);
    }
  };

  const handleRemove = (layoutId: string, layoutName: string) => {
    showConfirm({
      title: 'Remove layout?',
      message: `Remove "${layoutName}"? This will delete the layout and its anchors. This cannot be undone.`,
      confirmLabel: 'Remove',
      variant: 'danger',
      onConfirm: () => removeSiteLayoutById(layoutId),
    });
  };

  // ── Empty state ─────────────────────────────────────────────────────────
  if (layouts.length === 0) {
    if (isReadOnly) {
      return (
        <div className="ep-ticket-content" data-testid="vector-layout-ticket-empty">
          <div className="ep-section-label">Vector Layouts</div>
          <p className="ep-empty-text">No vector layouts available in this project.</p>
        </div>
      );
    }
    // Owner empty-state copy preserved verbatim from F-01B.
    return (
      <div className="ep-ticket-content" data-testid="vector-layout-ticket-empty">
        <div className="ep-section-label">Import Vector PDF</div>
        <p className="ep-empty-text" style={{ marginBottom: 'var(--sl-space-3)' }}>
          Upload a site string layout PDF to extract text labels for map overlay and topology auto-fill.
        </p>
        <Button variant="secondary" size="sm" fullWidth onClick={() => openModal('layoutImport')} data-testid="vector-layout-ticket-import-empty">
          Import Vector Layout
        </Button>
      </div>
    );
  }

  const active = activeLayout!;
  const activePct = pctById.get(active.id) ?? null;
  const schemasCount = active.labelSchemas?.length ?? 0;

  // ── List state ──────────────────────────────────────────────────────────
  return (
    <div className="ep-ticket-content">
      <div
        className="ep-section-label-row"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <span className="ep-section-label">Vector Layouts ({layouts.length})</span>
        {!isReadOnly && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => openModal('layoutImport')}
            data-testid="vector-layout-ticket-import"
          >
            + Import
          </Button>
        )}
      </div>

      {ghostPlanId && !isReadOnly && (
        <div className="ep-warn-banner" data-testid="vector-layout-ghost-link">
          <strong>Plan sheet not linked.</strong>{' '}
          {repairTarget
            ? <>This layout was imported with a plan sheet that never finished saving, so finishing
                the anchors does not place or bake anything.</>
            : <>This layout was imported with a plan sheet that never finished saving. Import the
                sheet from the Plans Layout ticket and it will re-link itself.</>}
          {repairTarget && (
            <Button
              variant="secondary"
              size="sm"
              fullWidth
              disabled={repairing}
              onClick={handleRepairLink}
              data-testid="vector-layout-repair-link"
            >
              {repairing ? 'Linking…' : `Link to "${repairTarget.name}"`}
            </Button>
          )}
        </div>
      )}

      {/* ── Layout cards ── */}
      <div className="vlt-cards">
        {layouts.map((layout) => {
          const isActive = layout.id === active.id;
          const pct = pctById.get(layout.id) ?? null;
          return (
            <div
              key={layout.id}
              role="button"
              tabIndex={0}
              className={`vlt-card${isActive ? ' vlt-card--active' : ''}`}
              data-testid="layout-row"
              data-layout-id={layout.id}
              data-active={isActive ? 'true' : 'false'}
              title={layout.name}
              onClick={() => setActiveLayoutId(layout.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setActiveLayoutId(layout.id);
                }
              }}
            >
              <span className="vlt-card__dot" />
              <span className="vlt-card__name">{layout.name}</span>
              <span className="vlt-card__meta">{layout.textEntities.length}</span>
              {pct != null && (
                <span className="vlt-card__pct" style={{ color: pctColor(pct) }}>
                  ✓{pct}%
                </span>
              )}
              {!isReadOnly && (
                <button
                  type="button"
                  className="vlt-card__remove"
                  aria-label={`Remove ${layout.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(layout.id, layout.name);
                  }}
                >
                  ×
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Active layout tool accordion (owners only — F-02E-2B: every
             section contains mutation surfaces; visitors keep card
             selection so they can switch which layout draws on the map) ── */}
      {!isReadOnly && (
        <>
          {/* Setup */}
          <div className="editor-section">
            <button className="editor-section__header" onClick={() => toggleSection('setup')}>
              <span className="editor-section__title">Setup</span>
              {openSection !== 'setup' && (
                <span className="editor-section__summary">
                  {active.anchors.length} anchors
                  {active.transform
                    ? ` · RMSE ${active.transform.residualMeters.toFixed(1)}m`
                    : ' · not registered'}
                </span>
              )}
              <Chevron open={openSection === 'setup'} />
            </button>
            <div className={`editor-section__body${openSection === 'setup' ? '' : ' editor-section__body--collapsed'}`}>
              <div className="editor-section__content">
                <Button
                  variant="secondary"
                  size="sm"
                  fullWidth
                  onClick={() => useUIStore.getState().setLayoutAnchorMode(true)}
                  data-testid="vector-layout-ticket-anchors"
                >
                  Place Anchors ({active.anchors.length})
                </Button>
                {active.anchors.length < 2 && (
                  <p className="ep-empty-text" style={{ marginTop: 'var(--sl-space-1)' }}>
                    Place at least 2 anchors to register on map.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Labels — Families → Schema → Patterns, one workflow */}
          <div className="editor-section">
            <button className="editor-section__header" onClick={() => toggleSection('labels')}>
              <span className="editor-section__title">Labels</span>
              {openSection !== 'labels' && (
                <span className="editor-section__summary">
                  {activePct != null ? `✓${activePct}% classified` : 'no labels'}
                  {schemasCount > 0 && ` · ${schemasCount} schema${schemasCount > 1 ? 's' : ''}`}
                </span>
              )}
              <Chevron open={openSection === 'labels'} />
            </button>
            <div className={`editor-section__body${openSection === 'labels' ? '' : ' editor-section__body--collapsed'}`}>
              <div className="editor-section__content">
                <div className="vlt-sub-label" style={{ marginTop: 0 }}>Label Families</div>
                <LabelFamiliesPanel />
                <div className="vlt-sub-label">Label Schema</div>
                {/* Keyed by the persisted schema fingerprint: the panel
                    snapshots labelSchemas into local state on mount, so an
                    externally added schema (Label Families "Draft schema")
                    must remount it to appear. */}
                <LabelSchemaPanel
                  key={(active.labelSchemas ?? []).map((s) => s.sampleLabel).join('|')}
                />
                <div className="vlt-sub-label">Advanced</div>
                <Button variant="secondary" size="sm" fullWidth onClick={() => openModal('classificationConfig')}>
                  Edit Patterns
                </Button>
              </div>
            </div>
          </div>

          {/* Module Grid */}
          <div className="editor-section">
            <button className="editor-section__header" onClick={() => toggleSection('grid')}>
              <span className="editor-section__title">Module Grid</span>
              {openSection !== 'grid' && (
                <span className="editor-section__summary">
                  {active.moduleGridConfig
                    ? `${active.moduleGridConfig.moduleCount} modules, ${active.moduleGridConfig.moduleWidth}×${active.moduleGridConfig.moduleHeight}m`
                    : 'Not configured'}
                </span>
              )}
              <Chevron open={openSection === 'grid'} />
            </button>
            <div className={`editor-section__body${openSection === 'grid' ? '' : ' editor-section__body--collapsed'}`}>
              <div className="editor-section__content">
                <ModuleGridPanel />
              </div>
            </div>
          </div>

          {/* Display */}
          <div className="editor-section">
            <button className="editor-section__header" onClick={() => toggleSection('display')}>
              <span className="editor-section__title">Display</span>
              {openSection !== 'display' && (
                <span className="editor-section__summary">
                  {!active.transform
                    ? 'register first'
                    : active.labelVisible === false
                      ? 'hidden'
                      : `labels ${Math.round(active.labelOpacity * 100)}% · img ${Math.round(active.imageOpacity * 100)}%`}
                </span>
              )}
              <Chevron open={openSection === 'display'} />
            </button>
            <div className={`editor-section__body${openSection === 'display' ? '' : ' editor-section__body--collapsed'}`}>
              <div className="editor-section__content">
                {!active.transform ? (
                  <p className="ep-empty-text">Register the layout (Setup → anchors) to control its display.</p>
                ) : (
                  <>
                    {/* Per-layout master visibility: labels + module grid +
                        string lines of THIS layout only. */}
                    <label className="ortho-item" style={{ padding: '4px 0', display: 'flex', alignItems: 'center', gap: 'var(--sl-space-2)', fontSize: 'var(--sl-text-xs)', color: 'var(--sl-text-secondary)', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={active.labelVisible !== false}
                        onChange={() =>
                          useProjectStore.getState().updateSiteLayoutDisplay({
                            labelVisible: active.labelVisible === false,
                          })
                        }
                        data-testid="vector-layout-ticket-visible"
                      />
                      Show this layout on the map
                    </label>
                    <div className="ortho-item" style={{ padding: '4px 0' }}>
                      <label style={{ fontSize: 'var(--sl-text-xs)', color: 'var(--sl-text-secondary)' }}>
                        Labels opacity
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="1"
                        value={Math.round(active.labelOpacity * 100)}
                        onChange={(e) =>
                          useProjectStore.getState().updateSiteLayoutDisplay({
                            labelOpacity: Number(e.target.value) / 100,
                          })
                        }
                        className="overlay-opacity"
                      />
                    </div>
                    <div className="ortho-item" style={{ padding: '4px 0' }}>
                      <label style={{ fontSize: 'var(--sl-text-xs)', color: 'var(--sl-text-secondary)' }}>
                        Image opacity
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="1"
                        value={Math.round(active.imageOpacity * 100)}
                        onChange={(e) =>
                          useProjectStore.getState().updateSiteLayoutDisplay({
                            imageOpacity: Number(e.target.value) / 100,
                          })
                        }
                        className="overlay-opacity"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
